/**
 * Countdown timer enhancer for snippets/countdown-timer.liquid.
 *
 * Rendered by sections/announcement-bar.liquid AND sections/dynamic-banner.liquid.
 * DOM-only, outside CCP: no event bus, no PPD, no md-config-* imports.
 *
 * Contract (see the snippet's doc block):
 * - Root: `.countdown-timer[data-ends-at]` where data-ends-at is the absolute end
 *   as Unix SECONDS.
 * - Visible units are days / hours / minutes. There is deliberately NO seconds
 *   unit: per-second motion is auto-updating content under WCAG 2.2.2. The
 *   display updates once per minute, scheduled for the exact moment the
 *   remaining-minute count changes, and is always recomputed from Date.now()
 *   (no decrementing counter, no rAF), so throttled tabs never drift.
 * - Minutes round UP: an active countdown never shows all zeros.
 * - Digits are aria-hidden; this file never touches ARIA live state.
 * - Expiry boundary is `now >= endsAt` (shared with the slide-expiry guard).
 *   At zero: hide the whole root, or with `data-stay-at-zero` keep 00s and swap
 *   the static sentence to its "Ended" variant. Then dispatch a bubbling
 *   `countdown:ended` CustomEvent on the root.
 * - A root removed from the DOM (Theme Editor section reload) stops its timer.
 *
 * The snippet may be rendered several times per page, so this file can be
 * evaluated more than once; the window guard and per-root flag make that safe.
 */
(function () {
  'use strict';

  if (window.MdCountdownTimer) {
    window.MdCountdownTimer.initAll(document);
    return;
  }

  var SELECTOR = '.countdown-timer[data-ends-at]';
  var UNITS = ['days', 'hours', 'minutes'];
  var MINUTE_MS = 60000;

  /**
   * Split a millisecond duration into zero-padded day/hour/minute strings.
   * Partial minutes round UP (30s left reads 00:00:01).
   */
  function getRemainingParts(msLeft) {
    var ms = Math.max(0, Number(msLeft) || 0);
    var totalMinutes = Math.ceil(ms / MINUTE_MS);
    var parts = {
      days: Math.floor(totalMinutes / 1440),
      hours: Math.floor((totalMinutes % 1440) / 60),
      minutes: totalMinutes % 60,
    };
    UNITS.forEach(function (unit) {
      parts[unit] = String(parts[unit]).padStart(2, '0');
    });
    return parts;
  }

  /** Milliseconds until the displayed (rounded-up) minute count next changes. */
  function msUntilNextChange(msLeft) {
    var remainder = msLeft % MINUTE_MS;
    return remainder === 0 ? MINUTE_MS : remainder;
  }

  function initCountdown(root) {
    if (!root || root.dataset.countdownInitialized) return;
    var endsAtMs = Number(root.dataset.endsAt) * 1000;
    if (!Number.isFinite(endsAtMs) || endsAtMs <= 0) return;
    root.dataset.countdownInitialized = 'true';

    var numbers = {};
    UNITS.forEach(function (unit) {
      numbers[unit] = root.querySelector(
        '[data-countdown-unit="' + unit + '"]'
      );
    });
    var stayAtZero = root.hasAttribute('data-stay-at-zero');
    var timeoutId = null;

    function stop() {
      if (timeoutId !== null) clearTimeout(timeoutId);
      timeoutId = null;
    }

    function render(parts) {
      UNITS.forEach(function (unit) {
        var el = numbers[unit];
        if (el && el.textContent !== parts[unit]) el.textContent = parts[unit];
      });
    }

    function finish() {
      stop();
      render(getRemainingParts(0));
      if (stayAtZero) {
        var active = root.querySelector('[data-countdown-text="active"]');
        var ended = root.querySelector('[data-countdown-text="ended"]');
        if (active && ended) {
          active.hidden = true;
          ended.hidden = false;
        }
      } else {
        root.hidden = true;
      }
      root.dataset.countdownEnded = 'true';
      root.dispatchEvent(
        new CustomEvent('countdown:ended', {
          bubbles: true,
          detail: { endsAt: endsAtMs / 1000, stayAtZero: stayAtZero },
        })
      );
    }

    function tick() {
      timeoutId = null;
      // Theme Editor re-renders replace the node: a detached root stops for good.
      if (!root.isConnected) return;
      var msLeft = endsAtMs - Date.now();
      if (msLeft <= 0) {
        finish();
        return;
      }
      render(getRemainingParts(msLeft));
      timeoutId = setTimeout(tick, msUntilNextChange(msLeft));
    }

    root._mdCountdownStop = stop;
    tick();
  }

  function initAll(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    host.querySelectorAll(SELECTOR).forEach(initCountdown);
  }

  function stopAll(scope) {
    if (!scope || !scope.querySelectorAll) return;
    scope.querySelectorAll(SELECTOR).forEach(function (root) {
      if (typeof root._mdCountdownStop === 'function') root._mdCountdownStop();
    });
  }

  window.MdCountdownTimer = {
    getRemainingParts: getRemainingParts,
    msUntilNextChange: msUntilNextChange,
    initCountdown: initCountdown,
    initAll: initAll,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Theme Editor re-renders a section in place: start new roots, stop old ones.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    stopAll(event.target);
  });
})();
